import type { AssignmentVersion } from "@/lib/api/assignments";
import { deliverableLabel } from "../work/work-facts";
import { changedFacts } from "./coursework-history";
import { assignmentNumber } from "../assignment-workspace-context";
import { atCourseTime } from "./use-team-names";

type Name = (userId: string | null | undefined) => string | null;

/**
 * What was published, when, by whom and why, newest first (S1 step 8). The
 * teaching reason is team-only: this renders on teaching surfaces alone.
 */
export function PublicationHistory({ versions, timezone, name }: { versions: AssignmentVersion[]; timezone: string; name: Name }) {
  if (!versions.length) return null;
  const ordered = [...versions].sort((a, b) => a.version_no - b.version_no);
  return (
    <ol className="grid gap-3 text-ui" aria-label="Publications">
      {ordered.map((version, index) => ({ version, before: ordered[index - 1] })).reverse().map(({ version, before }) => {
        const changed = changedFacts(version, before);
        const who = name(version.actor_user_id);
        return (
          <li key={version.id} className="grid gap-1 border-t border-rule pt-3 first:border-t-0 first:pt-0">
            <p className="font-medium text-foreground">
              Version {version.version_no} published{who ? ` by ${who}` : ""}, {atCourseTime(version.created_at, timezone)}
            </p>
            <p className="text-muted-foreground">
              {before ? (changed.length ? `${changed.join(", ")} changed.` : "Republished without a change to its terms.") : "First published."}
            </p>
            {version.reason && <p className="text-foreground">Reason: {version.reason}</p>}
            <details>
              <summary className="inline-flex min-h-11 cursor-pointer items-center text-primary">Terms in version {version.version_no}</summary>
              <div className="grid gap-1 pb-2">
                <p className="text-foreground [overflow-wrap:anywhere]" dir="auto">{version.title}</p>
                <p className="text-muted-foreground">
                  {version.due_at ? `Due ${atCourseTime(version.due_at, timezone)}` : "No due date"}
                  {version.points !== null && ` · ${assignmentNumber(version.points)} points`}
                  {` · ${deliverableLabel(version.expected_deliverable)}`}
                </p>
                {version.instructions && <p dir="auto" className="whitespace-pre-wrap break-words text-muted-foreground">{version.instructions}</p>}
              </div>
            </details>
          </li>
        );
      })}
    </ol>
  );
}
